import { DeleteTwoTone, DownloadOutlined, ExclamationCircleTwoTone } from '@ant-design/icons';
import { Button, Popconfirm, Table, Tooltip } from 'antd';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { isEmpty } from 'underscore';
import useGetSampleDataMutation from '../../../queries/useGetSampleDataMutation';
import { CartTableItem } from '../../../types/home';
import { downloadFile } from '../../../utils/downloadFile';
import { useSelectedDataset } from '../../layout/Header/components/SearchBar/useSelectedDataset';
import CartDetails from './CartDetails';
import styles from './ShoppingCart.module.scss';

const ERROR_COLUMN = 'Status' as const;

export default function CartTable(props: {
  tableData: CartTableItem[];
  delete: (item: CartTableItem) => void;
  loading: boolean;
}) {
  const { tableData, loading, delete: deleteItem } = props;
  const { onChange } = useSelectedDataset();
  const [pendingSampleDownloads, setPendingSampleDownloads] = useState<Record<string, boolean>>({});

  const { mutateAsync: triggerGetSampleData } = useGetSampleDataMutation({
    onSuccess: downloadFile,
  });

  const handleDelete = useCallback(
    (item: CartTableItem) => {
      deleteItem(item);
    },
    [deleteItem]
  );

  const errorColumn = useMemo(
    () => ({
      title: ERROR_COLUMN,
      dataIndex: 'hasError',
      key: 'hasError',
      className: '',
      width: undefined,
      render: (_text: any, record: CartTableItem, _index: any) => (
        <Fragment>
          {record.hasError ? (
            <Button
              key={Math.random()}
              type="link"
              icon={<ExclamationCircleTwoTone style={{ color: '#666666' }} />}
              style={{ padding: 0 }}
              loading={record.isPending}
            >
              {record.isPending ? 'Pending' : 'Failed'}
            </Button>
          ) : null}
        </Fragment>
      ),
    }),
    []
  );

  const onPointNameClick = useCallback(
    (record: CartTableItem) => {
      if (isEmpty(record.datasetId) || !record.datasetId) {
        return;
      }
      onChange(record.datasetId);
    },
    [onChange]
  );

  const getPendingSampleDownloadKey = useCallback(
    (record: CartTableItem) => String(record.datasetId ?? record.acronym),
    []
  );

  const isRequestPending = useCallback(
    (record: CartTableItem) => {
      return Boolean(pendingSampleDownloads[getPendingSampleDownloadKey(record)]);
    },
    [getPendingSampleDownloadKey, pendingSampleDownloads]
  );

  const handleSampleDownload = useCallback(
    async (record: CartTableItem) => {
      const pendingKey = getPendingSampleDownloadKey(record);

      setPendingSampleDownloads((currentPendingDownloads) => ({
        ...currentPendingDownloads,
        [pendingKey]: true,
      }));

      try {
        await triggerGetSampleData({ datasetAcronym: record.acronym });
      } finally {
        setPendingSampleDownloads((currentPendingDownloads) => {
          const { [pendingKey]: _removedPendingKey, ...remainingPending } = currentPendingDownloads;

          return remainingPending;
        });
      }
    },
    [getPendingSampleDownloadKey, triggerGetSampleData]
  );

  const columns = useMemo(
    () => [
      {
        title: 'Point Name',
        dataIndex: 'pointName',
        key: 'pointName',
        className: 'PointNameColumn',
      },
      {
        title: 'Category',
        dataIndex: 'category',
        key: 'category',
      },
      {
        title: 'Dataset',
        dataIndex: 'acronym',
        key: 'acronym',
        className: 'DatasetColumn',
        render: (_text: any, record: CartTableItem, _index: any) => (
          <Tooltip title={'Click to see metadata'}>
            <span
              id={`Cart-Dataset_Link_${record.datasetId}`}
              onClick={() => onPointNameClick(record)}
            >
              {record.acronym ? record.acronym : record.Dataset}
            </span>
          </Tooltip>
        ),
      },
      {
        title: 'Sample data (.zip)',
        key: 'sample',
        render: (_text: any, record: CartTableItem, _index: any) => (
          <Button
            id={`Cart-Dataset_Download_Button_${record.datasetId}`}
            type="primary"
            icon={<DownloadOutlined />}
            size={'small'}
            onClick={() => handleSampleDownload(record)}
            disabled={isRequestPending(record)}
            loading={isRequestPending(record)}
          >
            Download
          </Button>
        ),
      },
      {
        title: 'Location',
        dataIndex: 'Location',
        key: 'Location',
      },
      {
        title: 'Time',
        dataIndex: 'TimeStr',
        key: 'TimeStr',
        width: 99,
      },
      {
        title: 'Price',
        dataIndex: 'Price',
        key: 'Price',
        render: (_text: any, record: CartTableItem, _index: number) =>
          `€ ${record.Price.toFixed(2)}`,
      },
      {
        title: 'Operation',
        dataIndex: 'Operation',
        key: 'Operation',
        width: '125px',
        render: (_text: any, record: CartTableItem, _index: any) => (
          <Popconfirm
            placement="left"
            title="Are you sure you want to remove this item?"
            onConfirm={() => handleDelete(record)}
            okButtonProps={{
              id: `Cart-Dataset_Remove_Confirm_Yes`,
            }}
            cancelButtonProps={{
              id: `Cart-Dataset_Remove_Confirm_No`,
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button
              id={'Cart-Dataset_Remove_Button'}
              data-testid="Cart-Dataset_Remove_Button"
              key={Math.random()}
              type="link"
              icon={<DeleteTwoTone style={{ color: '#666666' }} />}
              style={{ padding: 0 }}
            >
              REMOVE
            </Button>
          </Popconfirm>
        ),
      },
    ],
    [handleDelete, handleSampleDownload, isRequestPending, onPointNameClick]
  );

  const finalColumns = useMemo(() => {
    const hasErrors = tableData?.some((item: CartTableItem) => item.hasError);
    const baseColumns = columns.filter((column) => column.title !== ERROR_COLUMN);

    return hasErrors ? [...baseColumns, errorColumn] : baseColumns;
  }, [columns, errorColumn, tableData]);

  return (
    <Table
      size="small"
      bordered
      dataSource={tableData}
      columns={finalColumns}
      className={styles.cartTable}
      pagination={{ defaultPageSize: 5 }}
      expandedRowRender={(record: CartTableItem) => (
        <CartDetails data={record.Detail}></CartDetails>
      )}
      expandedRowKeys={tableData.filter((d) => d.showDetail).map((d) => d.key)}
      expandIconColumnIndex={-1}
      expandIcon={() => null}
      loading={loading}
      scroll={{ x: 'max-content' }}
    />
  );
}
